import { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import axiosInstance from "../../../Config/axiosInstance";
import "./ClientPortfolioContracts.css";

function ClientPortfolioContracts({ clientData }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState("");

  const [opportunityRows, setOpportunityRows] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(true);
  const [, setOpportunitiesError] = useState("");

  useEffect(() => {
    const fetchContracts = async () => {
      if (!clientData?.id) {
        setRows([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axiosInstance.get(
          `/client-contracts/?client=${clientData.id}`
        );

        const contracts = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
          ? response.data.results
          : [];

        const mappedRows = contracts.map((contract, index) => ({
          id: contract.id ?? index,
          project_code_label: contract.project_code_label || "-",
          nr: contract.nr || "-",
          object: contract.object || "-",
          sign_date: contract.sign_date
            ? new Date(contract.sign_date).toLocaleDateString("ro-RO")
            : "-",
          total_system_contract: contract.total_system_contract || "-",
        }));

        setRows(mappedRows);
      } catch (err) {
        console.error("Error loading contracts:", err);
        setError("Unable to load contracts. Please try again later.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [clientData?.id]);

  useEffect(() => {
    const fetchOpportunities = async () => {
      if (!clientData?.id) {
        setOpportunityRows([]);
        setLoadingOpportunities(false);
        return;
      }

      try {
        setLoadingOpportunities(true);
        setOpportunitiesError("");

        const response = await axiosInstance.get(
          `/opportunities/?client=${clientData.id}`
        );

        const opportunities = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
          ? response.data.results
          : [];

        const mappedRows = opportunities.map((opportunity, index) => ({
          id: opportunity.id ?? index,
          code_opportunity: opportunity.code_opportunity || "-",
          project_name: opportunity.project_name || "-",
          project_code_label: opportunity.project_code_label || "-",
          subproject_code_label: opportunity.subproject_code_label || "-",
          issued_offer_value_current:
            opportunity.issued_offer_value_current || "-",
          chance_materialization: opportunity.chance_materialization || "-",
          status_offer: opportunity.status_offer || "-",
        }));

        setOpportunityRows(mappedRows);
      } catch (err) {
        console.error("Error loading opportunities:", err);
        setOpportunitiesError(
          "Unable to load opportunities. Please try again later."
        );
        setOpportunityRows([]);
      } finally {
        setLoadingOpportunities(false);
      }
    };

    fetchOpportunities();
  }, [clientData?.id]);

  const formatContractValue = (input) => {
    const rawValue =
      input && typeof input === "object" && "value" in input
        ? input.value
        : input;

    if (!rawValue) return "-";

    const text = String(rawValue).trim();
    const match = text.match(/^([\d.,]+)\s*([A-Za-z]{3})?$/);

    if (!match) return text;

    const numberPart = match[1];
    const currency = match[2] || "";

    const normalizedNumber = numberPart.replace(/,/g, "");
    const numericValue = parseFloat(normalizedNumber);

    if (Number.isNaN(numericValue)) return text;

    const formattedNumber = numericValue.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return currency ? `${formattedNumber} ${currency}` : formattedNumber;
  };

  const contractColumns = useMemo(() => [
    {
      field: "project_code_label",
      headerName: "Project Code",
      flex: 1.3,
      minWidth: 220,
      renderHeader: () => (
        <div className="header-with-icon">
          <BusinessCenterOutlinedIcon className="header-icon" />
          <span>Project Code</span>
        </div>
      ),
    },
    {
      field: "nr",
      headerName: "Contract No",
      flex: 0.9,
      minWidth: 150,
      renderHeader: () => (
        <div className="header-with-icon">
          <NumbersOutlinedIcon className="header-icon" />
          <span>Contract No</span>
        </div>
      ),
    },
    {
      field: "object",
      headerName: "Object",
      flex: 1.5,
      minWidth: 240,
      renderHeader: () => (
        <div className="header-with-icon">
          <ArticleOutlinedIcon className="header-icon" />
          <span>Object</span>
        </div>
      ),
    },
    {
      field: "sign_date",
      headerName: "Sign Date",
      flex: 0.9,
      minWidth: 140,
      renderHeader: () => (
        <div className="header-with-icon">
          <TodayOutlinedIcon className="header-icon" />
          <span>Sign Date</span>
        </div>
      ),
    },
    {
      field: "total_system_contract",
      headerName: "Value",
      flex: 1.1,
      minWidth: 170,
      renderHeader: () => (
        <div className="header-with-icon">
          <PaidOutlinedIcon className="header-icon" />
          <span>Value</span>
        </div>
      ),
      renderCell: (params) => (
        <span className="cell-text value-text">
          {formatContractValue(params.value)}
        </span>
      ),
    },
  ], []);

  const opportunityColumns = useMemo(() => [
    {
      field: "code_opportunity",
      headerName: "Opportunity Code",
      flex: 1,
      minWidth: 180,
      renderHeader: () => (
        <div className="header-with-icon">
          <NumbersOutlinedIcon className="header-icon" />
          <span>Opportunity Code</span>
        </div>
      ),
    },
    {
      field: "project_name",
      headerName: "Project Name",
      flex: 1.5,
      minWidth: 240,
      renderHeader: () => (
        <div className="header-with-icon">
          <WorkOutlineOutlinedIcon className="header-icon" />
          <span>Project Name</span>
        </div>
      ),
    },
    {
      field: "project_code_label",
      headerName: "Project Code",
      flex: 1.2,
      minWidth: 200,
      renderHeader: () => (
        <div className="header-with-icon">
          <BusinessCenterOutlinedIcon className="header-icon" />
          <span>Project Code</span>
        </div>
      ),
    },
    {
      field: "subproject_code_label",
      headerName: "Subproject Code",
      flex: 1.2,
      minWidth: 200,
      renderHeader: () => (
        <div className="header-with-icon">
          <AccountTreeOutlinedIcon className="header-icon" />
          <span>Subproject Code</span>
        </div>
      ),
    },
    {
      field: "issued_offer_value_current",
      headerName: "Issued Offer Value",
      flex: 1.1,
      minWidth: 170,
      renderHeader: () => (
        <div className="header-with-icon">
          <PaidOutlinedIcon className="header-icon" />
          <span>Issued Offer Value</span>
        </div>
      ),
      renderCell: (params) => (
        <span className="cell-text value-text">
          {formatContractValue(params.value)}
        </span>
      ),
    },
    {
      field: "chance_materialization",
      headerName: "Chance of Materialization",
      flex: 1.4,
      minWidth: 130,
      renderHeader: () => (
        <div className="header-with-icon">
          <PercentOutlinedIcon className="header-icon" />
          <span>Chance of Materialization</span>
        </div>
      ),
    },
    {
      field: "status_offer",
      headerName: "Offer Status",
      flex: 1,
      minWidth: 160,
      renderHeader: () => (
        <div className="header-with-icon">
          <FlagOutlinedIcon className="header-icon" />
          <span>Offer Status</span>
        </div>
      ),
    },
  ], []);

  const dataGridSx = {
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f9fafb",
      borderBottom: "2px solid #e5e7eb",
    },
  };

  return (
    <div className="client-contracts-page">
      
      {/* CONTRACTS */}
      <div className="contracts-card">
        <div className="contracts-card-header">
          <div className="header-icon-wrapper">
            <DescriptionIcon className="card-icon" />
          </div>
          <h3 className="card-title">
            Associated contracts linked to project code
          </h3>
        </div>

        <DataGrid
          rows={rows}
          columns={contractColumns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          sx={dataGridSx}
        />
      </div>

      {/* OPPORTUNITIES */}
      <div className="contracts-card">
        <div className="contracts-card-header">
          <div className="header-icon-wrapper">
            <WorkOutlineOutlinedIcon className="card-icon" />
          </div>
          <h3 className="card-title">
            Opportunities associated with this client
          </h3>
        </div>

        <DataGrid
          rows={opportunityRows}
          columns={opportunityColumns}
          loading={loadingOpportunities}
          autoHeight
          disableRowSelectionOnClick
          sx={dataGridSx}
        />
      </div>

    </div>
  );
}

export default ClientPortfolioContracts;